/*
  Fundpop draws its crowdfunding progress bar onto product cards from its app
  embed, but skips that work entirely on product pages. Campaign cards shown
  there (related products) therefore never got a bar. This renders Fundpop's
  own markup from the same proxy data, following the same rules, so those cards
  match the rest of the store. Off product pages it stays out of Fundpop's way.
*/
(function (global) {
  if (global.RandamuFundpopCardProgress) return;

  var PROXY = '/apps/campaign_proxy/api/proxy/';
  var CACHE_TTL_MS = 60 * 1000;
  var CARD_SELECTOR = 'product-card[data-fundpop-handle]';
  var PROGRESS_SELECTOR = '.fundpop-product-card-progress';
  var SECOND = 1000;
  var MINUTE = 60 * SECOND;
  var HOUR = 60 * MINUTE;
  var DAY = 24 * HOUR;

  // Fundpop's defaults; the admin's productCardProgressLabels override them.
  var DEFAULT_LABELS = {
    day: 'day',
    days: 'days',
    hour: 'hour',
    hours: 'hours',
    minute: 'minute',
    minutes: 'minutes',
    second: 'second',
    seconds: 'seconds',
    ended: 'Ended',
    ongoing: 'Ongoing',
    soldSingular: 'Sold',
    soldPlural: 'Sold',
    leftSingular: 'Left',
    leftPlural: 'Left',
  };

  /* Same check Fundpop uses to decide it will not touch product cards. */
  function isProductPage(pathname) {
    return /\/products\/[^/?#]+/i.test(pathname || '');
  }

  function isPastEnd(campaign, nowMs) {
    return campaign.endTime ? new Date(campaign.endTime).getTime() < nowMs : false;
  }

  /* Fundpop's rule for which campaigns get a card bar at all. */
  function isVisible(campaign, nowMs) {
    switch (campaign.state) {
      case 'Active':
        return true;
      case 'Pending':
        return campaign.isPendingVisible === true;
      case 'Finished':
      case 'Stretched':
        return campaign.isEndedVisible === true;
      case 'Disabled':
        return false;
      default:
        return !(isPastEnd(campaign, nowMs) && campaign.isEndedVisible !== true);
    }
  }

  /* An ended campaign that still takes orders reads "Ongoing" instead of "Ended". */
  function isOngoingAfterEnd(campaign, nowMs) {
    return (
      !campaign.preventSalesWhileStretched &&
      campaign.isEndedVisible === true &&
      (campaign.state === 'Stretched' || (campaign.state === 'Active' && isPastEnd(campaign, nowMs)))
    );
  }

  /* First visible campaign per product handle, as Fundpop picks it. */
  function campaignsByHandle(campaigns, nowMs) {
    var map = {};
    (campaigns || []).forEach(function (campaign) {
      if (!isVisible(campaign, nowMs)) return;
      (campaign.products || []).forEach(function (product) {
        if (!map[product.handle]) map[product.handle] = campaign;
      });
    });
    return map;
  }

  /* The app embed's bar styling wins over the admin settings, as in Fundpop. */
  function mergeSettings(settings, embed) {
    settings = settings || {};
    var bar = (embed && embed.progressBar) || {};
    var progress = settings.campaignProgress || {};
    return {
      campaignProgress: Object.assign({}, progress, {
        progressBarColor: bar.color || progress.progressBarColor,
        progressBarOfProductColor: bar.productContributionColor || progress.progressBarOfProductColor,
        progressBarHeight: bar.height || progress.progressBarHeight,
        progressBarBorderRadius: bar.borderRadius || progress.progressBarBorderRadius,
        font: bar.font || progress.font,
        fontSize: bar.fontSize || progress.fontSize,
      }),
      productCardProgressLabels: Object.assign({}, DEFAULT_LABELS, settings.productCardProgressLabels),
    };
  }

  /* Largest non-zero unit only, e.g. "16 days"; null once nothing is left. */
  function formatRemaining(ms, labels) {
    var units = [
      [Math.floor(ms / DAY), 'day', 'days'],
      [Math.floor((ms % DAY) / HOUR), 'hour', 'hours'],
      [Math.floor((ms % HOUR) / MINUTE), 'minute', 'minutes'],
      [Math.floor((ms % MINUTE) / SECOND), 'second', 'seconds'],
    ];
    for (var i = 0; i < units.length; i++) {
      var value = units[i][0];
      if (value > 0) return value + ' ' + labels[value === 1 ? units[i][1] : units[i][2]];
    }
    return null;
  }

  function formatCount(n) {
    return new Intl.NumberFormat('en-US').format(n);
  }

  function formatMoney(n, currency) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n) + ' ' + (currency || 'USD');
  }

  function round2(n) {
    return Number(n.toFixed(2));
  }

  function unitsRaised(campaign) {
    return (campaign.totalUnitsSold || 0) + (campaign.initialRaisedAmount || 0);
  }

  function moneyRaised(campaign) {
    return (campaign.totalRevenue || 0) + (campaign.initialRaisedAmount || 0);
  }

  function isMoneyGoal(campaign) {
    return campaign.goalMode === 'currency' || campaign.goalMode === 'money';
  }

  function raised(campaign) {
    return isMoneyGoal(campaign) ? moneyRaised(campaign) : unitsRaised(campaign);
  }

  function productDetail(campaign, productId) {
    return (campaign.productDetails || {})[productId] || {};
  }

  /* Per-product stock cap, when the merchant switched on "units left". */
  function unitsLimit(campaign, productId, progress) {
    var own = (progress.singleProductSettings || {})[productId];
    return own && own.campaignId === campaign.id && own.availableQuantity > 0 && progress.showUnitsLeft ? own : null;
  }

  function contributionPercent(campaign, productId, limit) {
    var detail = productDetail(campaign, productId);
    if (limit) {
      return Math.min(((detail.totalUnitsSold || 0) / limit.availableQuantity) * 100, 100).toFixed(2) + '%';
    }
    var total =
      campaign.products.length === 1
        ? (campaign.goalMode === 'units' ? detail.totalUnitsSold || 0 : detail.totalRevenue || 0) +
          (campaign.initialRaisedAmount || 0)
        : raised(campaign);
    return Math.min((total / campaign.goal) * 100, 100).toFixed(2) + '%';
  }

  function progressText(campaign, productId, progress, limit, labels) {
    var single = campaign.products.length === 1;
    var detail = productDetail(campaign, productId);
    var initial = campaign.initialRaisedAmount || 0;
    var firstVariant = (campaign.products[0].variants || [])[0] || {};
    var unitPrice = firstVariant.priceOnCampaign || 0;
    var sold = function (n) {
      return formatCount(n) + ' ' + (n === 1 ? labels.soldSingular : labels.soldPlural);
    };
    var left = function (n) {
      return formatCount(n) + ' ' + (n === 1 ? labels.leftSingular : labels.leftPlural);
    };

    if (limit) {
      var ownSold = detail.totalUnitsSold || 0;
      return sold(ownSold) + ' · ' + left(Math.max(0, limit.availableQuantity - ownSold));
    }

    switch (progress.displayProgressOptions) {
      case 'unitSold':
        if (!single) return sold(unitsRaised(campaign));
        var initialUnits = campaign.goalMode === 'units' ? initial : Math.floor(initial / unitPrice) || 0;
        return sold((detail.totalUnitsSold || campaign.totalUnitsSold) + initialUnits);
      case 'moneyRaised':
        if (!single) return formatMoney(round2(moneyRaised(campaign)), campaign.currencyUnit);
        var initialMoney = campaign.goalMode === 'units' ? initial * unitPrice : initial;
        return formatMoney(round2((detail.totalRevenue || 0) + initialMoney), campaign.currencyUnit);
      case 'progress':
        return contributionPercent(campaign, productId, limit);
      case 'campaignProgress':
        return ((raised(campaign) / Number(campaign.goal)) * 100).toFixed(2) + '%';
      default:
        if (campaign.goalMode === 'units') {
          if (!single) return sold(unitsRaised(campaign));
          return sold((detail.totalUnitsSold || campaign.totalUnitsSold) + initial);
        }
        if (!single) return formatMoney(round2(moneyRaised(campaign)), campaign.currencyUnit);
        return formatMoney(
          round2((detail.totalRevenue || campaign.totalRevenue) + initial),
          campaign.currencyUnit
        );
    }
  }

  /*
    Units sold for one product, counted the way the "Sold" text counts them: a single-product
    campaign includes the merchant's initial raised amount (converted to units for money goals).
  */
  function unitsSold(campaign, handle) {
    var products = campaign.products || [];
    var product = products.filter(function (p) {
      return p.handle === handle;
    })[0];
    if (!product) return null;

    var detail = productDetail(campaign, product.id);
    if (products.length !== 1) return detail.totalUnitsSold || 0;

    var initial = campaign.initialRaisedAmount || 0;
    var unitPrice = ((product.variants || [])[0] || {}).priceOnCampaign || 0;
    var initialUnits = campaign.goalMode === 'units' ? initial : Math.floor(initial / unitPrice) || 0;
    return (detail.totalUnitsSold || campaign.totalUnitsSold || 0) + initialUnits;
  }

  /* Text and bar width for one card, computed exactly as Fundpop does. */
  function buildView(campaign, handle, settings, nowMs) {
    var product = (campaign.products || []).filter(function (p) {
      return p.handle === handle;
    })[0];
    if (!product) return null;

    var progress = settings.campaignProgress;
    var labels = settings.productCardProgressLabels;
    var limit = unitsLimit(campaign, product.id, progress);
    var remaining = new Date(campaign.endTime).getTime() - nowMs;
    var timeText =
      remaining > 0
        ? formatRemaining(remaining, labels) || labels.ended
        : isOngoingAfterEnd(campaign, nowMs)
          ? labels.ongoing
          : labels.ended;

    var width;
    if (limit) {
      width = Math.min(((productDetail(campaign, product.id).totalUnitsSold || 0) / limit.availableQuantity) * 100, 100);
    } else {
      var goal = Number(campaign.goal) || 0;
      width = goal === 0 ? 0 : Math.min((raised(campaign) / goal) * 100, 100);
    }

    return {
      progressText: progressText(campaign, product.id, progress, limit, labels),
      timeText: timeText,
      progressWidth: width,
      showProductContribution: progress.displayProgressOptions === 'progress',
      productContributionPercent: parseFloat(contributionPercent(campaign, product.id, limit)),
    };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* Fundpop's card markup, so its stylesheet and the theme's CSS apply unchanged. */
  function createProgressEl(doc, campaign, handle, settings, nowMs) {
    var view = buildView(campaign, handle, settings, nowMs);
    if (!view) return null;

    var progress = settings.campaignProgress;
    var font = escapeHtml('font-family: ' + progress.font + '; font-size: ' + progress.fontSize + ';');
    var contribution = view.showProductContribution
      ? '<div class="fundpop-product-card-progress__product-contribution" style="width: ' +
        view.productContributionPercent +
        '%; background-color: ' +
        escapeHtml(progress.progressBarOfProductColor) +
        ';"></div>'
      : '';

    var el = doc.createElement('div');
    el.id = 'custom-app-embed';
    el.className = 'fundpop-product-card-progress';
    el.setAttribute('data-product-handle', handle);
    // The card's inline script that normally sets these never runs for cards
    // injected by innerHTML, and without order the bar sits above the image.
    el.style.order = '2';
    el.style.padding = '0';
    el.innerHTML =
      '<div class="fundpop-product-card-progress__bar" style="height: ' +
      escapeHtml(progress.progressBarHeight) +
      '; border-radius: ' +
      escapeHtml(progress.progressBarBorderRadius) +
      '; position: relative; box-sizing: border-box; padding: 2px; box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.10);">' +
      '<div class="fundpop-product-card-progress__fill" style="width: ' +
      view.progressWidth +
      '%; background-color: ' +
      escapeHtml(progress.progressBarColor) +
      ';"></div>' +
      contribution +
      '</div>' +
      '<div class="fundpop-product-card-progress__metrics">' +
      '<div class="fundpop-product-card-progress__row">' +
      '<p class="fundpop-product-card-progress__progress" style="' +
      font +
      '">' +
      escapeHtml(view.progressText) +
      '</p>' +
      '<p class="fundpop-product-card-progress__time" style="' +
      font +
      '">' +
      escapeHtml(view.timeText) +
      '</p>' +
      '</div>' +
      '</div>';
    return el;
  }

  function locale() {
    var raw = (global.Shopify && global.Shopify.locale) || document.documentElement.lang || 'en';
    return String(raw).trim().replace('_', '-');
  }

  function fetchJson(path) {
    var url = PROXY + path + '?locale=' + encodeURIComponent(locale());
    var load = function () {
      return fetch(url).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      });
    };
    var cache = global.RandamuApiCache;
    return cache && typeof cache.getOrFetch === 'function'
      ? cache.getOrFetch(url, load, { ttlMs: CACHE_TTL_MS })
      : load();
  }

  function readEmbedSettings() {
    try {
      var node = document.getElementById('fundpop-app-embed-settings');
      return node ? JSON.parse(node.textContent || '{}') : {};
    } catch (e) {
      return {};
    }
  }

  function loadState() {
    return Promise.all([
      fetchJson('campaigns-optimized'),
      fetchJson('product-campaign-progress-settings-optimized'),
    ]).then(function (results) {
      var raw = results[1];
      if (!raw || !raw.campaignProgress || raw.campaignProgress.progressBarEnabled === false) return null;
      return {
        settings: mergeSettings(raw, readEmbedSettings()),
        byHandle: campaignsByHandle(results[0], Date.now()),
      };
    });
  }

  function decorateCards(state) {
    var nowMs = Date.now();
    var cards = document.querySelectorAll(CARD_SELECTOR);
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector(PROGRESS_SELECTOR)) continue;
      var handle = card.getAttribute('data-fundpop-handle');
      var campaign = state.byHandle[handle];
      if (!campaign) continue;
      var el = createProgressEl(document, campaign, handle, state.settings, nowMs);
      if (el) card.appendChild(el);
    }
  }

  function init() {
    if (!isProductPage(global.location && global.location.pathname)) return;

    var ready = null;
    var timer = null;
    function refresh() {
      // Only pay for the proxy calls once a campaign card is actually on the page.
      if (!document.querySelector(CARD_SELECTOR)) return;
      if (!ready) {
        ready = loadState().catch(function () {
          return null;
        });
      }
      ready.then(function (state) {
        if (state) decorateCards(state);
      });
    }

    refresh();
    // Related products arrive later via fetch + innerHTML.
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(refresh, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  global.RandamuFundpopCardProgress = {
    isProductPage: isProductPage,
    campaignsByHandle: campaignsByHandle,
    mergeSettings: mergeSettings,
    formatRemaining: formatRemaining,
    buildView: buildView,
    unitsSold: unitsSold,
    createProgressEl: createProgressEl,
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
